import React from "react";
import { NavLink, useParams, useLocation } from "react-router-dom";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import useCsr from "../hooks/useCsr";
import { CSR_STATUS } from "../utils/Constants";

const SidebarCSR = () => {
  const { studyId } = useParams();
  const location = useLocation();
  const { isPathAllowed } = useFeatureFlags();
  const { mainStatus } = useCsr();

  const handleClick = e => {
    e.preventDefault();
  };

  const app = location.pathname.split("/")[1];

  const navLinks =
    app === "ls"
      ? [
          { path: "select-study", label: "Select Study", stepNumber: 1 },
          {
            path: "lay-summary-questions",
            label: "Wizard Questions",
            stepNumber: 2,
          },
          {
            path: "lay-summary-file",
            label: "Lay Summary File",
            stepNumber: 3,
          },
        ]
      : [
          { path: "summary", label: "Study Name", stepNumber: 1 },
          { path: "file-upload", label: "File Upload", stepNumber: 2 },
          {
            path: "study-design-demographics",
            label: "Wizard Questions",
            stepNumber: 3,
            subItems: [
              {
                stepNumber: 4,
                path: "study-design-demographics",
                label: "Study Design",
              },
              {
                stepNumber: 5,
                path: "efficacy-analysis",
                label: "Efficacy",
              },
              {
                stepNumber: 6,
                path: "safety-analysis",
                label: "Safety",
              },
              {
                stepNumber: 7,
                path: "stdpharmacokinetics",
                label: "Standard PK",
              },
              {
                stepNumber: 8,
                path: "comppharmacokinetics",
                label: "Comparative PK",
              },
              {
                stepNumber: 9,
                path: "bioavailability",
                label: "Bioavailability",
              },
              {
                stepNumber: 10,
                path: "stdpharmacodynamics",
                label: "Standard PD",
              },
              {
                stepNumber: 11,
                path: "comppharmacodynamics",
                label: "Comparative PD",
              },
            ],
          },
          { path: "data-mapping", label: "Data Mapping", stepNumber: 12 },
          { path: "file", label: "CSR file", stepNumber: 13 },
          { path: "advancedsearch", label: "TriloAI", stepNumber: 14 },
        ];

  const isTriloAiEnabled =
    app === "csr" &&
    studyId &&
    studyId !== "new" &&
    mainStatus === CSR_STATUS.ready;

  const getNavLinkProps = path => {
    let fullPath = "";
    let allowed = false;
    if (app === "ls") {
      fullPath = studyId ? `/ls/${studyId}/${path}` : `/ls/new/${path}`;
      allowed = isPathAllowed(path);
    } else {
      fullPath = studyId ? `/csr/${studyId}/${path}` : `/csr/new/${path}`;
      allowed = isPathAllowed(path);
    }

    const isTriloAiLink = app === "csr" && path === "advancedsearch";
    const isDisabled =
      !studyId || !allowed || (isTriloAiLink && !isTriloAiEnabled);

    return {
      to: allowed ? fullPath : "/",
      onClick: isDisabled ? handleClick : undefined,
    };
  };

  const isNavLinkDisabled = path => {
    if (app !== "csr" || path !== "advancedsearch") return false;
    return !isTriloAiEnabled;
  };

  const getTitle = () => {
    if (app === "ls") {
      return "Create a new lay summary report";
    } else {
      // Return "New CSR" when there's no studyId, "CSR" when there is one
      return studyId ? "CSR" : "New CSR";
    }
  };

  // Get all steps including sub-items for step calculation
  const getAllSteps = () => {
    const allSteps = [];
    navLinks.forEach(({ stepNumber, subItems }) => {
      allSteps.push({ stepNumber, isSubItem: false });
      if (subItems) {
        subItems.forEach(subItem => {
          if (subItem.stepNumber) {
            allSteps.push({ stepNumber: subItem.stepNumber, isSubItem: true });
          }
        });
      }
    });
    return allSteps.sort((a, b) => a.stepNumber - b.stepNumber);
  };

  // Determine the current active step based on the route
  const getCurrentStep = () => {
    const currentPath = location.pathname;

    // Table Analysis pages belong to the Advanced Search step
    const isTableAnalysisPage =
      currentPath.includes("table-analysis-questions") ||
      currentPath.includes("table-analysis-ai-response");
    if (app === "csr" && isTableAnalysisPage) {
      const advancedSearchIndex = navLinks.findIndex(
        ({ path }) => path === "advancedsearch"
      );
      if (advancedSearchIndex >= 0) {
        return navLinks[advancedSearchIndex].stepNumber;
      }
    }

    // First check sub-items
    for (const navLink of navLinks) {
      if (navLink.subItems) {
        for (const subItem of navLink.subItems) {
          if (subItem.stepNumber) {
            const subPathPattern = new RegExp(
              `/(?:csr|ls)/(?:new|[0-9a-fA-F]+)/${subItem.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
            );
            if (subPathPattern.test(currentPath)) {
              return subItem.stepNumber;
            }
          }
        }
      }
    }

    // Then check main steps
    const stepIndex = navLinks.findIndex(({ path }) => {
      const pathPattern = new RegExp(
        `/(?:csr|ls)/(?:new|[0-9a-fA-F]+)/${path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
      );
      return pathPattern.test(currentPath);
    });

    // Return step number (1-indexed) or default to 1
    return stepIndex >= 0 ? navLinks[stepIndex].stepNumber : 1;
  };

  const currentStep = getCurrentStep();

  // Determine step state: completed, active, or inactive
  const getStepState = stepNumber => {
    if (stepNumber < currentStep) {
      return "completed";
    } else if (stepNumber === currentStep) {
      return "active";
    } else {
      return "inactive";
    }
  };

  // Check if progress line should be visible (only between completed and active steps)
  const shouldShowProgressLine = (
    stepNumber,
    isSubItem = false,
    isLastSubItem = false,
    parentHasSubItems = false
  ) => {
    const state = getStepState(stepNumber);
    const allSteps = getAllSteps();
    const maxStepNumber = Math.max(...allSteps.map(s => s.stepNumber));

    // Don't show line if step is inactive
    if (state === "inactive") {
      return false;
    }

    // For sub-items, show line if not the last sub-item and step is completed or active
    if (isSubItem) {
      return !isLastSubItem && (state === "completed" || state === "active");
    }

    // For main step 3 (Wizard Questions) with sub-items, show line to connect to sub-items
    // if step 3 is completed or active
    if (parentHasSubItems && stepNumber === 3) {
      return state === "completed" || state === "active";
    }

    // For all other main steps, show line if completed or active and not the last step
    return (
      (state === "completed" || state === "active") &&
      stepNumber < maxStepNumber
    );
  };

  // Convert sub-item step numbers (4-11) to a dot for display
  const getDisplayStepNumber = stepNumber => {
    if (stepNumber >= 4 && stepNumber <= 11) {
      return "";
    }
    if (stepNumber >= 12) {
      return stepNumber - 8;
    }
    return stepNumber;
  };

  // Check if a sub-item is active
  const isSubItemActive = subItemPath => {
    const currentPath = location.pathname;
    const subPathPattern = new RegExp(
      `/(?:csr|ls)/(?:new|[0-9a-fA-F]+)/${subItemPath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
    );
    return subPathPattern.test(currentPath);
  };

  return (
    <div className={"sidebar__csr"}>
      <h2 className={"title"}>{getTitle()}</h2>
      <div className={"progress-indicator"}>
        {navLinks.map(
          ({ path, label, stepNumber, subItems }) =>
            isPathAllowed(path) && (
              <div
                key={path}
                className={
                  subItems ? "progress-step-wrapper--has-sub-items" : ""
                }>
                <div
                  className={`progress-step progress-step--${getStepState(stepNumber)} ${subItems ? "progress-step--has-sub-items" : ""}`}>
                  <div className={"progress-step__content"}>
                    <div className={"progress-step__icon"}>
                      {getStepState(stepNumber) === "completed" ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="11" fill="#4AB8AA" />
                          <path
                            d="M7 12L10.5 15.5L17 9"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <div className={"progress-step__number"}>
                          {getDisplayStepNumber(stepNumber)}
                        </div>
                      )}
                    </div>
                    <NavLink
                      {...getNavLinkProps(path)}
                      isActive={
                        app === "csr" && path === "advancedsearch"
                          ? (match, loc) => {
                              const pathname = loc.pathname || "";
                              return (
                                !!match ||
                                pathname.includes("table-analysis-questions") ||
                                pathname.includes("table-analysis-ai-response")
                              );
                            }
                          : undefined
                      }
                      className={({ isActive }) =>
                        `progress-step__link ${isActive ? "progress-step__link--active" : ""} ${isNavLinkDisabled(path) ? "progress-step__link--disabled" : ""}`
                      }
                      end={false}>
                      {label}
                    </NavLink>
                  </div>
                  {shouldShowProgressLine(
                    stepNumber,
                    false,
                    false,
                    !!subItems
                  ) && (
                    <div
                      className={`progress-step__line progress-step__line--main ${subItems ? "progress-step__line--with-sub-items" : ""}`}
                    />
                  )}
                </div>
                {subItems &&
                  subItems.map((subItem, subItemIndex) => {
                    const subItemActive = isSubItemActive(subItem.path);
                    const subItemStepNumber = subItem.stepNumber;
                    const subItemState = subItemStepNumber
                      ? getStepState(subItemStepNumber)
                      : "inactive";
                    const isLastSubItem = subItemIndex === subItems.length - 1;

                    return (
                      <div key={subItem.path + subItem.label}>
                        <div
                          className={`progress-step progress-step__sub-item progress-step--${subItemState}`}>
                          <div className={"progress-step__content"}>
                            <div className={"progress-step__icon"}>
                              {subItemStepNumber &&
                              subItemState === "completed" ? (
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="11"
                                    fill="#4AB8AA"
                                  />
                                  <path
                                    d="M7 12L10.5 15.5L17 9"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : subItemStepNumber ? (
                                <div className={"progress-step__number"}>
                                  {getDisplayStepNumber(subItemStepNumber)}
                                </div>
                              ) : null}
                            </div>
                            <NavLink
                              {...getNavLinkProps(subItem.path)}
                              className={({ isActive }) =>
                                `progress-step__link progress-step__link--sub ${
                                  isActive || subItemActive
                                    ? "progress-step__link--active"
                                    : ""
                                }`
                              }
                              end={false}>
                              {subItem.label}
                            </NavLink>
                          </div>
                          {shouldShowProgressLine(
                            subItemStepNumber,
                            true,
                            isLastSubItem
                          ) && (
                            <div
                              className={
                                "progress-step__line progress-step__line--sub"
                              }
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )
        )}
      </div>
    </div>
  );
};

export default SidebarCSR;
